import { useEffect, useState } from "react";
import { Link, Navigate, useNavigate } from "react-router-dom";
import { useAuth } from "../lib/auth";
import api, { formatApiErrorDetail } from "../lib/api";
import { Input } from "../components/ui/input";
import { Textarea } from "../components/ui/textarea";
import { Button } from "../components/ui/button";
import { Label } from "../components/ui/label";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "../components/ui/tabs";
import { Card } from "../components/ui/card";
import { Trash2, Plus, LogOut, Flame } from "lucide-react";
import { toast } from "sonner";

/**
 * Generic CRUD manager component.
 */
function CrudManager({ label, path, fields, testKey }) {
  const [items, setItems] = useState([]);
  const [form, setForm] = useState({});
  const [loading, setLoading] = useState(false);

  const reset = () => setForm(Object.fromEntries(fields.map((f) => [f.name, ""])));

  const load = async () => {
    try { const { data } = await api.get(`/${path}`); setItems(data); } catch {}
  };

  useEffect(() => { reset(); load(); /* eslint-disable-next-line */ }, []);

  const submit = async (e) => {
    e.preventDefault();
    setLoading(true);
    try {
      await api.post(`/${path}`, form);
      toast.success(`${label} added`);
      reset(); load();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Failed");
    } finally { setLoading(false); }
  };

  const remove = async (id) => {
    if (!window.confirm("Delete this item?")) return;
    try { await api.delete(`/${path}/${id}`); toast.success("Deleted"); load(); }
    catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail)); }
  };

  return (
    <div className="grid grid-cols-1 lg:grid-cols-[420px_1fr] gap-6">
      <Card className="p-6 bg-[#132a4a] border-zinc-800">
        <h3 className="font-display text-xl text-white mb-4 flex items-center gap-2"><Plus className="w-4 h-4 text-gold" /> New {label}</h3>
        <form onSubmit={submit} className="space-y-3" data-testid={`admin-${testKey}-form`}>
          {fields.map((f) => (
            <div key={f.name}>
              <Label className="text-zinc-400 text-xs font-accent">{f.label}</Label>
              {f.type === "textarea" ? (
                <Textarea rows={4} required={f.required} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  data-testid={`admin-${testKey}-${f.name}`} className="mt-1 bg-black/40 border-zinc-800" />
              ) : (
                <Input type={f.type || "text"} required={f.required} value={form[f.name] || ""} onChange={(e) => setForm({ ...form, [f.name]: e.target.value })}
                  data-testid={`admin-${testKey}-${f.name}`} className="mt-1 bg-black/40 border-zinc-800" />
              )}
            </div>
          ))}
          <Button type="submit" disabled={loading} data-testid={`admin-${testKey}-submit`} className="w-full bg-gold text-black hover:bg-slate-200">
            {loading ? "Saving…" : `Add ${label}`}
          </Button>
        </form>
      </Card>
      <Card className="p-6 bg-[#132a4a] border-zinc-800">
        <h3 className="font-display text-xl text-white mb-4">{label}s ({items.length})</h3>
        <div className="space-y-3 max-h-[600px] overflow-y-auto">
          {items.length === 0 && <p className="text-zinc-500 text-sm">None yet.</p>}
          {items.map((it) => (
            <div key={it.id} className="border hairline rounded-lg p-4 bg-black/30 flex items-start justify-between gap-4">
              <div className="min-w-0">
                <div className="font-display text-white truncate">{it.title || it.name}</div>
                <div className="text-xs text-zinc-500 mt-1 truncate">{it.speaker || it.author || it.leader || it.date || it.address || it.message || ""}</div>
              </div>
              <button onClick={() => remove(it.id)} data-testid={`admin-${testKey}-delete-${it.id}`}
                className="p-2 rounded-md border border-zinc-800 text-red-400 hover:border-red-500 hover:text-red-500 transition-colors">
                <Trash2 className="w-4 h-4" />
              </button>
            </div>
          ))}
        </div>
      </Card>
    </div>
  );
}

const CONFIGS = {
  sermons: { label: "Sermon", path: "sermons", testKey: "sermons", fields: [
    { name: "title", label: "TITLE", required: true },
    { name: "speaker", label: "SPEAKER", required: true },
    { name: "date", label: "DATE (YYYY-MM-DD)", type: "date", required: true },
    { name: "scripture", label: "SCRIPTURE REFERENCE" },
    { name: "youtube_url", label: "YOUTUBE EMBED URL" },
    { name: "audio_url", label: "AUDIO URL" },
    { name: "description", label: "DESCRIPTION", type: "textarea" },
  ]},
  events: { label: "Event", path: "events", testKey: "events", fields: [
    { name: "title", label: "TITLE", required: true },
    { name: "date", label: "DATE & TIME (ISO)", required: true },
    { name: "location", label: "LOCATION" },
    { name: "image_url", label: "IMAGE URL" },
    { name: "description", label: "DESCRIPTION", type: "textarea" },
  ]},
  ministries: { label: "Ministry", path: "ministries", testKey: "ministries", fields: [
    { name: "name", label: "NAME", required: true },
    { name: "leader", label: "LEADER" },
    { name: "image_url", label: "IMAGE URL" },
    { name: "description", label: "DESCRIPTION", type: "textarea", required: true },
  ]},
  blog: { label: "Post", path: "blog", testKey: "blog", fields: [
    { name: "title", label: "TITLE", required: true },
    { name: "author", label: "AUTHOR", required: true },
    { name: "image_url", label: "IMAGE URL" },
    { name: "excerpt", label: "EXCERPT", type: "textarea" },
    { name: "content", label: "CONTENT", type: "textarea", required: true },
  ]},
  announcements: { label: "Announcement", path: "announcements", testKey: "announcements", fields: [
    { name: "title", label: "TITLE", required: true },
    { name: "message", label: "MESSAGE", type: "textarea", required: true },
  ]},
  "prayer-centers": { label: "Prayer Center", path: "prayer-centers", testKey: "prayer-centers", fields: [
    { name: "name", label: "NAME", required: true },
    { name: "address", label: "ADDRESS", required: true },
    { name: "city", label: "CITY" },
    { name: "country", label: "COUNTRY" },
    { name: "contact", label: "CONTACT PERSON" },
    { name: "phone", label: "PHONE" },
    { name: "schedule", label: "SCHEDULE" },
  ]},
  gallery: { label: "Image", path: "gallery", testKey: "gallery", fields: [
    { name: "title", label: "TITLE" },
    { name: "image_url", label: "IMAGE URL", required: true },
  ]},
};

export default function AdminDashboard() {
  const { user, loading, logout } = useAuth();
  const nav = useNavigate();
  const [stats, setStats] = useState(null);
  const [contacts, setContacts] = useState([]);

  useEffect(() => {
    if (user && user.role === "admin") {
      api.get("/admin/stats").then((r) => setStats(r.data)).catch(() => {});
      api.get("/contact").then((r) => setContacts(r.data)).catch(() => {});
    }
  }, [user]);

  if (loading || user === null) return <div className="min-h-screen flex items-center justify-center text-zinc-400">Loading…</div>;
  if (!user || user.role !== "admin") return <Navigate to="/admin/login" replace />;

  const handleLogout = async () => { await logout(); nav("/admin/login"); };

  return (
    <div className="min-h-screen bg-[#0a1930]" data-testid="admin-dashboard">
      <header className="border-b hairline bg-black/70 backdrop-blur-xl sticky top-0 z-40">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/" className="flex items-center gap-3">
            <Flame className="w-5 h-5 text-gold" />
            <span className="font-display text-lg text-white">Council Admin</span>
          </Link>
          <div className="flex items-center gap-4">
            <span className="text-sm text-zinc-400 hidden sm:inline">{user.email}</span>
            <Button variant="outline" size="sm" onClick={handleLogout} data-testid="admin-logout"
              className="border-zinc-800 text-zinc-300 hover:bg-white/5"><LogOut className="w-4 h-4 mr-1" /> Sign out</Button>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 lg:px-10 py-10">
        <h1 className="font-display text-4xl text-white mb-2">Dashboard</h1>
        <p className="text-zinc-500 mb-8">Manage the content of the Council website.</p>

        {stats && (
          <div className="grid grid-cols-2 md:grid-cols-5 gap-3 mb-10">
            {[
              ["Sermons", stats.sermons], ["Events", stats.events], ["Ministries", stats.ministries],
              ["Prayer Centers", stats.prayer_centers], ["Donations", stats.donations],
            ].map(([k, v]) => (
              <div key={k} className="border hairline rounded-xl p-4 bg-[#132a4a]">
                <div className="font-accent text-[10px] text-gold">{k.toUpperCase()}</div>
                <div className="font-display text-3xl text-white mt-1">{v}</div>
              </div>
            ))}
          </div>
        )}

        <Tabs defaultValue="sermons" className="w-full">
          <TabsList className="bg-[#132a4a] border border-zinc-800 flex-wrap h-auto">
            <TabsTrigger value="sermons" data-testid="admin-tab-sermons">Sermons</TabsTrigger>
            <TabsTrigger value="events" data-testid="admin-tab-events">Events</TabsTrigger>
            <TabsTrigger value="ministries" data-testid="admin-tab-ministries">Ministries</TabsTrigger>
            <TabsTrigger value="blog" data-testid="admin-tab-blog">Blog</TabsTrigger>
            <TabsTrigger value="announcements" data-testid="admin-tab-announcements">Announcements</TabsTrigger>
            <TabsTrigger value="prayer-centers" data-testid="admin-tab-prayer-centers">Prayer Centers</TabsTrigger>
            <TabsTrigger value="gallery" data-testid="admin-tab-gallery">Gallery</TabsTrigger>
            <TabsTrigger value="messages" data-testid="admin-tab-messages">Messages</TabsTrigger>
          </TabsList>
          {Object.entries(CONFIGS).map(([key, cfg]) => (
            <TabsContent key={key} value={key} className="mt-6">
              <CrudManager {...cfg} />
            </TabsContent>
          ))}
          <TabsContent value="messages" className="mt-6">
            <Card className="p-6 bg-[#132a4a] border-zinc-800">
              <h3 className="font-display text-xl text-white mb-4">Contact Messages ({contacts.length})</h3>
              <div className="space-y-4 max-h-[600px] overflow-y-auto">
                {contacts.length === 0 && <p className="text-zinc-500 text-sm">No messages yet.</p>}
                {contacts.map((c) => (
                  <div key={c.id} className="border hairline rounded-lg p-4 bg-black/30">
                    <div className="flex items-center justify-between gap-4">
                      <div>
                        <div className="font-display text-white">{c.name}</div>
                        <div className="text-xs text-zinc-500">{c.email} · {c.subject || "No subject"}</div>
                      </div>
                      <div className="text-xs text-zinc-600">{new Date(c.created_at).toLocaleString()}</div>
                    </div>
                    <p className="text-sm text-zinc-300 mt-3 whitespace-pre-line">{c.message}</p>
                  </div>
                ))}
              </div>
            </Card>
          </TabsContent>
        </Tabs>
      </div>
    </div>
  );
}
